import type {
  AudienceDto,
  CreateAudienceDto,
} from "@intelligems/ig-types/dtos/audiences";
import type { CartTargetingDto } from "@intelligems/ig-types/dtos/cart-targeting";
import type { CheckoutBlockDto } from "@intelligems/ig-types/dtos/checkout-blocks";
import type { ComponentDto } from "@intelligems/ig-types/dtos/components";
import type { CreateExperienceCustomMetricDto } from "@intelligems/ig-types/dtos/experience-custom-metrics";
import type { CreateExperienceProductTargetingDto } from "@intelligems/ig-types/dtos/experience-product-targeting";
import type {
  CreateOnsiteInjectionDto,
  OnsiteInjectionDto,
} from "@intelligems/ig-types/dtos/onsite-injections";
import type { ExperienceFe } from "@intelligems/ig-types/fe-only/experiences";
import type { ExperiencePageTargetingFe } from "@intelligems/ig-types/fe-only/page-targeting";
import type { OfferDto } from "@intelligems/ig-types/dtos/offers";
import type { OnsiteEditDto } from "@intelligems/ig-types/dtos/onsite-edits";
import type { PutExperienceRequestBodyType } from "@intelligems/ig-types/functions/experiences/put-experience";
import type { RedirectDto } from "@intelligems/ig-types/dtos/redirects";
import type { UpsellProductRulesDto } from "@intelligems/ig-types/dtos/upsell-product-rules";
import type { UserInterfaceDto } from "@intelligems/ig-types/dtos/user-interfaces";
import type { VariationFe } from "@intelligems/ig-types/fe-only/variations";
import { createSelector } from "@reduxjs/toolkit";
import { formatAudienceDto } from "@src/api/formatters/from-dto/audience";
import { getExperienceDtoFromFe } from "@src/api/formatters/from-dto/experiences";
import { getVariationDtoFromFe } from "@src/api/formatters/from-dto/variations";
import type { RootState } from "@src/store/rootState";
import {
  getCartTargetingForPayload,
  getStoredCartTargetingForPayload,
} from "@src/store/slices/cartTargeting/selectors";
import { getExperienceProductTargetingForPayload } from "@src/store/slices/experienceProductTargeting/selectors";
import { getUserInterfacesPutPayload } from "@src/store/slices/experiences/util";
import {
  getStoredUpsellProductRulesForPayload,
  getUpsellProductRulesForPayload,
} from "@src/store/slices/upsellProductRules/selectors";

export const getPutPersonalizationPayload = createSelector(
  (state: RootState) => state.experience.experience as ExperienceFe,
  (state: RootState) => state.variations.variations,
  (state: RootState) => state.onsiteInjections.injections,
  (state: RootState) => state.onsiteEdits.allEdits,
  (state: RootState) => state.offers.offers,
  (state: RootState) => state.redirects.redirects,
  (state: RootState) => state.audience.audience as AudienceDto,
  (state: RootState) => state.pageTargeting.pageTargeting,
  (state: RootState) => state.userInterfaces.interfaces,
  (state: RootState) => state.experienceMetrics.customMetrics,
  (state: RootState) => state.checkoutBlocks.checkoutBlocks,
  (state: RootState) => state.checkoutBlocks.postPurchaseBlocks,
  getExperienceProductTargetingForPayload,
  getCartTargetingForPayload,
  getUpsellProductRulesForPayload,
  (state: RootState) => state.experienceComponents.components,
  (
    experience,
    variations,
    onsiteInjections,
    onsiteEdits,
    offers,
    redirects,
    audience,
    pageTargeting,
    userInterfaces,
    experienceCustomMetrics,
    checkoutBlocks,
    postPurchaseBlocks,
    experienceProductTargeting,
    cartTargeting,
    upsellProductRules,
    components
  ) => {
    return createPayload({
      experience,
      variations,
      onsiteInjections,
      onsiteEdits,
      offers,
      redirects,
      audience,
      pageTargeting,
      userInterfaces,
      experienceCustomMetrics,
      checkoutBlocks: [...checkoutBlocks, ...postPurchaseBlocks],
      experienceProductTargeting,
      cartTargeting,
      upsellProductRules,
      components,
    });
  }
);

export const getPutStoredPersonalizationPayload = createSelector(
  (state: RootState) => state.experience.storedExperience as ExperienceFe,
  (state: RootState) => state.variations.storedVariations,
  (state: RootState) => state.onsiteInjections.storedInjections,
  (state: RootState) => state.onsiteEdits.storedEdits,
  (state: RootState) => state.offers.storedOffers,
  (state: RootState) => state.redirects.storedRedirects,
  (state: RootState) => state.audience.storedAudience as AudienceDto,
  (state: RootState) => state.pageTargeting.storedPageTargeting,
  (state: RootState) => state.userInterfaces.storedInterfaces,
  (state: RootState) => state.experienceMetrics.storedCustomMetrics,
  (state: RootState) => state.checkoutBlocks.storedCheckoutBlocks,
  (state: RootState) => state.checkoutBlocks.storedPostPurchaseBlocks,
  (state: RootState) =>
    state.experienceProductTargeting.storedExperienceProductTargeting,
  getStoredCartTargetingForPayload,
  getStoredUpsellProductRulesForPayload,
  (state: RootState) => state.experienceComponents.storedComponents,
  (
    experience,
    variations,
    onsiteInjections,
    onsiteEdits,
    offers,
    redirects,
    audience,
    pageTargeting,
    userInterfaces,
    experienceCustomMetrics,
    checkoutBlocks,
    storedPostPurchaseBlocks,
    experienceProductTargeting,
    cartTargeting,
    upsellProductRules,
    components
  ) => {
    return createPayload({
      experience,
      variations,
      onsiteInjections,
      onsiteEdits,
      offers,
      redirects,
      audience,
      pageTargeting,
      userInterfaces,
      experienceCustomMetrics,
      checkoutBlocks: [...checkoutBlocks, ...storedPostPurchaseBlocks],
      experienceProductTargeting,
      cartTargeting,
      upsellProductRules,
      components,
    });
  }
);

function createPayload({
  experience,
  variations,
  onsiteInjections,
  onsiteEdits,
  offers,
  redirects,
  audience,
  pageTargeting,
  userInterfaces,
  experienceCustomMetrics,
  checkoutBlocks,
  experienceProductTargeting,
  cartTargeting,
  upsellProductRules,
  components,
}: {
  experience: ExperienceFe;
  variations: VariationFe[];
  onsiteInjections: OnsiteInjectionDto[] | CreateOnsiteInjectionDto[];
  onsiteEdits: OnsiteEditDto[];
  offers: OfferDto[];
  redirects: RedirectDto[];
  audience: AudienceDto;
  pageTargeting: ExperiencePageTargetingFe[];
  userInterfaces: UserInterfaceDto[] | undefined;
  experienceCustomMetrics: CreateExperienceCustomMetricDto[];
  checkoutBlocks: CheckoutBlockDto[];
  experienceProductTargeting: CreateExperienceProductTargetingDto[];
  cartTargeting: CartTargetingDto[];
  upsellProductRules: UpsellProductRulesDto[];
  components: ComponentDto[];
}): PutExperienceRequestBodyType | undefined {
  if (!experience) {
    return undefined;
  }

  try {
    return {
      experience: getExperienceDtoFromFe(experience, checkoutBlocks),
      variations: getVariationDtoFromFe(variations),
      onsiteEdits,
      onsiteInjections,
      offers,
      redirects,
      experiencePageTargeting:
        redirects.filter((redirect) => !redirect.isTemplateTest).length > 0
          ? []
          : pageTargeting,
      exclusionGroup: undefined,
      audience: formatAudienceDto(audience) as CreateAudienceDto,
      userInterfaces: getUserInterfacesPutPayload(userInterfaces),
      experienceKeyMetrics: [],
      experienceCustomMetrics,
      checkoutBlocks,
      experienceProductTargeting: experienceProductTargeting,
      cartTargeting,
      upsellProductRules,
      components,
    };
  } catch {
    return undefined;
  }
}
